import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import invalidateCalculatorsForPayment, { PaymentType } from 'modules/Tax/queries/invalidateCalculatorsForPayment';
import { MEMBER_TASKS_QUERY_KEY } from 'services/memberTasks/queries/useMemberTasks';

/**
 * Refetches the payment lists and invalidates the calculators a payment of this type feeds.
 *
 * The create and update mutations do this in their own `onSuccess`, but the receipt path never
 * reaches them — Uppy POSTs/PUTs the payment itself. `useListTaxPayments` has `staleTime: Infinity`,
 * so without this a payment recorded with a receipt stayed invisible until a reload.
 *
 * Pass `previousPaymentType` when an edit also moved the payment between tax types.
 */
export default function useRefreshPaymentCaches() {
    const queryClient = useQueryClient();

    return useCallback(
        async (paymentType?: PaymentType, previousPaymentType?: PaymentType) => {
            await queryClient.refetchQueries({ queryKey: ['listTaxPayments'] });
            await Promise.all([
                invalidateCalculatorsForPayment(queryClient, paymentType),
                ...(previousPaymentType && previousPaymentType !== paymentType
                    ? [invalidateCalculatorsForPayment(queryClient, previousPaymentType)]
                    : []),
                // Uppy posts the payment itself on this path, so the mutations' own invalidation
                // never runs — the task list has to be re-read from here too.
                queryClient.invalidateQueries({ queryKey: MEMBER_TASKS_QUERY_KEY }),
            ]);
        },
        [queryClient]
    );
}
